import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from '@/components/ui/dialog';
import { 
  Stethoscope, 
  Users, 
  Calendar, 
  FileText, 
  Shield, 
  Clock,
  Star,
  ArrowRight,
  Play,
  CheckCircle2,
  XCircle
} from 'lucide-react';
import WhatsAppButton from '@/components/ui/WhatsAppButton';

const LandingPage = () => {
  // Logo path - use logo.png from public folder, fallback to current icon if not available
  const logoPath = '/logo-new.png';
  const [logoError, setLogoError] = useState(false);
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const [billingCycle, setBillingCycle] = useState('monthly'); // 'monthly' or 'yearly'

  const plans = {
    silver: {
      name: 'Silver Plan',
      description: 'Essential doctor portal access for your practice',
      monthlyPrice: 2500,
      yearlyPrice: 2500 * 12, // Base yearly price (12 months)
      yearlyPlanDiscount: 10, // 10% discount on yearly plan
      icon: Shield,
      color: 'from-gray-600 to-gray-800',
      features: [
        { name: 'Doctor Portal Access', included: true },
        { name: 'Patient Management', included: true },
        { name: 'Appointment Scheduling', included: true },
        { name: 'Clinical Documentation', included: true },
        { name: 'Medical History Tracking', included: true },
        { name: 'Dental Examination Records', included: true },
        { name: 'Procedure Tracking', included: true },
        { name: 'Email to Patients', included: false },
        { name: 'SMS/WhatsApp Messages', included: false },
        { name: 'Patient Login Portal', included: false },
        { name: 'Patient Self-Booking', included: false },
        { name: 'Patient Record Access', included: false }
      ]
    },
    gold: {
      name: 'Gold Plan',
      description: 'Complete solution with patient engagement features',
      monthlyPrice: 3500,
      yearlyPrice: 3500 * 12, // Base yearly price (12 months)
      yearlyPlanDiscount: 15, // 15% discount on yearly plan
      icon: Star,
      color: 'from-yellow-500 to-orange-600',
      popular: true,
      features: [
        { name: 'Doctor Portal Access', included: true },
        { name: 'Patient Management', included: true },
        { name: 'Appointment Scheduling', included: true },
        { name: 'Clinical Documentation', included: true },
        { name: 'Medical History Tracking', included: true },
        { name: 'Dental Examination Records', included: true },
        { name: 'Procedure Tracking', included: true },
        { name: 'Email to Patients', included: true },
        { name: 'SMS/WhatsApp Messages', included: true },
        { name: 'Patient Login Portal', included: true },
        { name: 'Patient Self-Booking', included: true },
        { name: 'Patient Record Access', included: true }
      ]
    }
  };

  // Calculate discounted price for yearly billing
  const calculateYearlyPrice = (plan) => {
    let finalPrice = plan.yearlyPrice;
    let discountAmount = 0;
    let totalDiscountPercent = 0;

    if (plan.yearlyPlanDiscount && plan.yearlyPlanDiscount > 0) {
      // Apply yearly plan discount
      discountAmount = (finalPrice * plan.yearlyPlanDiscount) / 100;
      finalPrice = finalPrice - discountAmount;
      totalDiscountPercent = plan.yearlyPlanDiscount;
    }

    return {
      originalPrice: plan.yearlyPrice,
      finalPrice: Math.round(finalPrice),
      discountAmount: Math.round(discountAmount),
      totalDiscountPercent: Math.round(totalDiscountPercent * 10) / 10, // Round to 1 decimal
      monthlyEquivalent: Math.round(finalPrice / 12)
    };
  };

  const features = [
    {
      icon: Users,
      title: 'Multi-Role Access',
      description: 'Separate portals for patients, doctors, and administrators with role-based permissions.'
    },
    {
      icon: FileText,
      title: 'Digital Forms',
      description: 'Customizable dental health questionnaires with file upload capabilities.'
    },
    {
      icon: Calendar,
      title: 'Smart Scheduling',
      description: 'Intelligent appointment booking based on doctor availability and patient preferences.'
    },
    {
      icon: Shield,
      title: 'Secure & Compliant',
      description: 'HIPAA-compliant data storage with advanced security measures.'
    },
    {
      icon: Clock,
      title: 'Real-time Updates',
      description: 'Instant notifications for form submissions, appointments, and doctor feedback.'
    },
    {
      icon: Stethoscope,
      title: 'Clinical Workflow',
      description: 'Streamlined patient management with comprehensive medical record tracking.'
    }
  ];

  return (
    <>
      <Helmet>
        <title>Dental Hub - Modern Dental Practice Management System</title>
        <meta name="description" content="Comprehensive electronic health records system for dental practices. Manage patients, appointments, and medical records with our secure, HIPAA-compliant platform." />
      </Helmet>
      
      <div className="min-h-screen">
        {/* Navigation */}
        <nav className="fixed top-0 w-full z-50 glass-effect p-3">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-center items-center h-20">
              <motion.div 
                className="flex items-center justify-center"
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                {!logoError ? (
                  <img 
                    src={logoPath} 
                    alt="Dental Hub Logo" 
                    className="h-20 w-auto object-contain"
                    onError={() => setLogoError(true)}
                  />
                ) : (
                  <>
                   <Stethoscope className="h-20 w-20 text-blue-400" /> 
                   <span className="text-xl font-bold gradient-text">Dental Hub</span>
                  </>
                )}
              </motion.div>
              
               {/* <motion.div 
                className="flex items-center h-full"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                <Link to="/register/hospital">
                  <Button className="medical-gradient text-white hover:opacity-90">
                    Get Started
                  </Button>
                </Link>
              </motion.div>
              <motion.div 
                className="flex items-center h-full"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5 }}
              >
                <Link to="/admin/hub/login">
                  <Button variant="ghost" className="text-white hover:text-blue-400 text-sm">
                    Admin
                  </Button>
                </Link>
              </motion.div>  */}
            </div>
          </div>
        </nav>

        {/* Hero Section */}
        <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h1 className="text-5xl md:text-7xl font-bold mb-6">
                <span className="gradient-text">Modern Dental</span>
                <br />
                Practice Management
              </h1>
              <p className="text-xl md:text-2xl text-gray-300 mb-8 max-w-3xl mx-auto">
                Streamline your dental practice with our comprehensive EHR system. 
                Manage patients, appointments, and medical records all in one secure platform.
              </p>
              <h4 className="text-2xl md:text-2xl font-bold mb-4 gradient-text">
               Make Your Dental Clinic Digital Today - One Platform. Full Clinic Control !!
              </h4>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/register/hospital">
                  <Button size="lg" className="medical-gradient text-white hover:opacity-90 px-8 py-4 text-lg">
                    Start Free Trial
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="border-blue-400 text-blue-400 hover:bg-blue-400 hover:text-white px-8 py-4 text-lg"
                  onClick={() => setIsVideoModalOpen(true)}
                >
                  <Play className="mr-2 h-5 w-5" />
                  Watch Demo
                </Button>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Features Section */}
        <section className="py-5 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <motion.div
              className="text-center mb-16"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl md:text-5xl font-bold mb-4 gradient-text">
                Everything You Need
              </h2>
              <p className="text-xl text-gray-300 max-w-2xl mx-auto">
                Powerful features designed specifically for modern dental practices
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {features.map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <Card className="glass-effect border-gray-700 card-hover h-full">
                    <CardHeader>
                      <feature.icon className="h-12 w-12 text-blue-400 mb-4" />
                      <CardTitle className="text-white">{feature.title}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <CardDescription className="text-gray-300">
                        {feature.description}
                      </CardDescription>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="py-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <motion.div
              className="glass-effect rounded-2xl p-12 border-gray-700"
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
                <div>
                  <div className="text-4xl md:text-5xl font-bold gradient-text mb-2">50+</div>
                  <div className="text-gray-300">Dental Practices</div>
                </div>
                <div>
                  <div className="text-4xl md:text-5xl font-bold gradient-text mb-2">1K+</div>
                  <div className="text-gray-300">Patients Managed</div>
                </div>
                <div>
                  <div className="text-4xl md:text-5xl font-bold gradient-text mb-2">99.9%</div>
                  <div className="text-gray-300">Uptime Guarantee</div>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl md:text-5xl font-bold mb-6 gradient-text">
                Ready to Transform Your Practice?
              </h2>
              <p className="text-xl text-gray-300 mb-8">
                Join hundreds of dental practices already using Dental Hub
              </p>
              <Link to="/register/hospital">
                <Button size="lg" className="medical-gradient text-white hover:opacity-90 px-12 py-4 text-lg">
                  Register your Clinic Today
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </motion.div>
          </div>
        </section>

        {/* Pricing Section */}
        <section className="py-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <motion.div
              className="text-center mb-16"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl md:text-5xl font-bold mb-4 gradient-text">
                Simple, Transparent Pricing
              </h2>
              <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto">
                Choose the plan that fits your practice. All plans start with a free trial.
              </p>
              
              {/* Billing Cycle Toggle */}
              <div className="flex items-center justify-center gap-4 mb-8">
                <span className={`text-lg font-medium ${billingCycle === 'monthly' ? 'text-white' : 'text-gray-400'}`}>
                  Monthly
                </span>
                <button
                  onClick={() => setBillingCycle(billingCycle === 'monthly' ? 'yearly' : 'monthly')}
                  className="relative w-16 h-8 bg-gray-700 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <motion.div
                    className="absolute top-1 left-1 w-6 h-6 bg-blue-500 rounded-full"
                    animate={{ x: billingCycle === 'yearly' ? 32 : 0 }}
                    transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                  />
                </button>
                <span className={`text-lg font-medium ${billingCycle === 'yearly' ? 'text-white' : 'text-gray-400'}`}>
                  Yearly
                  <span className="ml-2 text-sm text-green-400">(⭐ Lower Cost per Month ⭐)</span>
                </span>
              </div>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {Object.entries(plans).map(([key, plan]) => {
                const Icon = plan.icon;
                const yearlyPricing = calculateYearlyPrice(plan);
                const price = billingCycle === 'monthly' ? plan.monthlyPrice : yearlyPricing.finalPrice;
                const monthlyEquivalent = billingCycle === 'yearly' ? yearlyPricing.monthlyEquivalent : price;
                
                return (
                  <motion.div
                    key={key}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: key === 'silver' ? 0 : 0.2 }}
                    viewport={{ once: true }}
                  >
                    <Card className={`glass-effect border-gray-700 card-hover relative h-full ${plan.popular ? 'border-yellow-500 border-2' : ''}`}>
                      {plan.popular && (
                        <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                          <span className="bg-gradient-to-r from-yellow-500 to-orange-600 text-white px-4 py-1 rounded-full text-sm font-bold">
                            Most Popular
                          </span>
                        </div>
                      )}
                      
                      <CardHeader className="text-center pb-8">
                        <div className={`mx-auto w-16 h-16 rounded-full bg-gradient-to-r ${plan.color} flex items-center justify-center mb-4`}>
                          <Icon className="h-8 w-8 text-white" />
                        </div>
                        <CardTitle className="text-3xl font-bold text-white mb-2">{plan.name}</CardTitle>
                        <CardDescription className="text-gray-300 text-lg">{plan.description}</CardDescription>
                        
                        <div className="mt-6">
                          {billingCycle === 'yearly' && yearlyPricing.totalDiscountPercent > 0 && (
                            <div className="mb-3">
                              <div className="flex items-center justify-center gap-2">
                                <span className="text-lg text-gray-400 line-through">
                                  ₹{yearlyPricing.originalPrice.toLocaleString('en-IN')}
                                </span>
                                <span className="text-lg font-bold text-green-400">
                                  Save {yearlyPricing.totalDiscountPercent}%
                                </span>
                              </div>
                            </div>
                          )}
                          <div className="flex items-baseline justify-center">
                            <span className="text-5xl font-bold text-white">₹{price.toLocaleString('en-IN')}</span>
                            <span className="text-gray-400 ml-2">/{billingCycle === 'monthly' ? 'month' : 'year'}</span>
                          </div>
                          {billingCycle === 'yearly' && (
                            <>
                              <p className="text-sm text-gray-400 mt-2">
                                ₹{monthlyEquivalent.toLocaleString('en-IN')}/month billed annually
                              </p>
                              {plan.yearlyPlanDiscount > 0 && (
                                <div className="mt-2">
                                  <p className="text-xs text-green-400">
                                    Yearly Plan Discount: {plan.yearlyPlanDiscount}% applied
                                  </p>
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      </CardHeader>
                      
                      <CardContent>
                        <ul className="space-y-4 mb-8">
                          {plan.features.map((feature, index) => (
                            <li key={index} className="flex items-start gap-3">
                              {feature.included ? (
                                <CheckCircle2 className="h-5 w-5 text-green-400 mt-0.5 flex-shrink-0" />
                              ) : (
                                <XCircle className="h-5 w-5 text-gray-600 mt-0.5 flex-shrink-0" />
                              )}
                              <span className={feature.included ? 'text-gray-300' : 'text-gray-600 line-through'}>
                                {feature.name}
                              </span>
                            </li>
                          ))}
                        </ul>
                        
                        <Link to="/register/hospital">
                          <Button
                            className={`w-full py-6 text-lg font-bold ${
                              plan.popular
                                ? 'bg-gradient-to-r from-yellow-500 to-orange-600 text-white hover:opacity-90'
                                : 'medical-gradient text-white hover:opacity-90'
                            }`}
                            size="lg"
                          >
                            Start Free Trial
                            <ArrowRight className="ml-2 h-5 w-5" />
                          </Button>
                        </Link>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </div>
            
            {/* Additional Info */}
            <motion.div
              className="mt-12 text-center"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              viewport={{ once: true }}
            >
              <Card className="glass-effect border-gray-700">
                <CardContent className="p-6">
                  <p className="text-gray-300 mb-4">
                    All plans include 24/7 support, regular updates, and secure data storage.
                  </p>
                  <div className="text-gray-400 text-sm">
                    Need help choosing? Contact us at{' '}
                    <br />
                    <a href="mailto:neinustech@gmail.com" className="text-blue-400 hover:text-blue-300 underline">
                    neinustech@gmail.com
                    </a>
                    <br />
                    <a href="mailto:contactus@neinus.com" className="text-blue-400 hover:text-blue-300 underline">
                    contactus@neinus.com
                    </a>
                    <br />
                    <div>India : +91 9841181493</div>
                    <div>USA   : +1 6238248761</div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </section>

        {/* YouTube Video Modal */}
        <Dialog open={isVideoModalOpen} onOpenChange={setIsVideoModalOpen}>
          <DialogContent className="max-w-4xl w-[95vw] p-0 bg-gray-900 border-gray-700">
            <DialogTitle className="sr-only">Watch Demo Video</DialogTitle>
            <div className="relative pt-[56.25%] w-full h-0 overflow-hidden">
              <iframe
                className="absolute top-0 left-0 w-full h-full"
                src="https://www.youtube.com/embed/Pvsvs8V16KI?autoplay=1"
                title="YouTube video player"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
              />
            </div>
          </DialogContent>
        </Dialog>

        {/* Footer */}
        <footer className="border-t border-gray-700 py-12 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto text-center">
            <div className="flex items-center justify-center mb-4">
              {!logoError ? (
                <img 
                  src={logoPath} 
                  alt="Dental Hub Logo" 
                  className="h-20  object-contain"
                  onError={() => setLogoError(true)}
                />
              ) : (
                <Stethoscope className="h-20 w-20 text-blue-400" />
              )}
            </div>
            <p className="text-gray-400 text-sm">
              ©2026 Dental Hub, Product of  <a 
                href="https://www.neinus.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-blue-400 hover:text-blue-300 underline transition-colors"
              >
                Neinus Technologies
              </a>. All rights reserved.
              
            </p>
          </div>
        </footer>

        {/* WhatsApp Chat & Call Buttons */}
        {/* WhatsApp message uses USA number, Call uses India number */}
        <WhatsAppButton 
          phoneNumber="+16238248761" 
          callNumber="+919629996774"
          message="Hello, I'd like to know more about Dental Hub services."
        />
      </div>
    </>
  );
};

export default LandingPage;
